import { useEffect, useState } from "react";
import { useBrandKits } from "../api/brandKits";
import { CONTEXT_COPY } from "../copy/errors";
import { Input } from "../ui";

const HEX = /^#[0-9a-fA-F]{6}$/;

/** A colour for a surface: the default brand kit's palette as swatches, then
 *  a hex field. A swatch, or a `#rrggbb` value on blur or Enter, sets the
 *  colour at once (stored lower-case); anything else says how a colour is
 *  written and changes nothing. */
export function ColourControl(props: {
  /** "#rrggbb", or null when the surface has no colour of its own yet. */
  value: string | null;
  onChange(hex: string): void;
}): JSX.Element {
  const { value, onChange } = props;
  const kits = useBrandKits();
  const palette = kits.data?.items.find((kit) => kit.is_default)?.palette ?? [];
  const swatches = [
    ...new Set(
      palette.map((c) => c.hex.toLowerCase()).filter((hex) => HEX.test(hex)),
    ),
  ];
  const [text, setText] = useState(value ?? "");
  const [bad, setBad] = useState(false);
  // A colour set from outside (a swatch, Reset, another surface) fills the
  // field.
  useEffect(() => {
    setText(value ?? "");
    setBad(false);
  }, [value]);

  const commit = () => {
    const typed = text.trim();
    if (typed === "" || typed.toLowerCase() === (value ?? "")) {
      setBad(false);
      return;
    }
    if (!HEX.test(typed)) {
      setBad(true);
      return;
    }
    setBad(false);
    onChange(typed.toLowerCase());
  };

  return (
    <div className="flex flex-col gap-2" data-testid="colour-control">
      <span className="text-sm font-semibold text-slate-800">Colour</span>
      <div className="flex flex-wrap items-center gap-2">
        {swatches.map((hex) => (
          <button
            key={hex}
            type="button"
            aria-label={"Use " + hex}
            title={hex}
            className="h-11 w-11 rounded-full border border-slate-300 p-0 sm:h-8 sm:w-8"
            style={{ background: hex }}
            onClick={() => {
              setBad(false);
              onChange(hex);
            }}
          />
        ))}
        <Input
          type="text"
          aria-label="Colour"
          placeholder="#4d2c91"
          className="min-h-[44px] w-32 sm:min-h-0"
          value={text}
          invalid={bad}
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit();
            }
          }}
        />
      </div>
      {bad ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {CONTEXT_COPY.colour}
        </p>
      ) : null}
    </div>
  );
}
